import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LabeledCode, ListFilterDef, ListFilterValue, ListOp } from '../filterTypes';
import { createLabelResolver } from '../../../lib/labeledCode';
import type { FilterFieldProps } from '../FilterField.types';
import { FieldHint } from '../FilterFieldHelpers';
import { menuItemSx, menuPaperSx, selectSx } from '../styles/filterColumnSx';
import '../styles/filterPanel.css';

export function ListFilterInput({
  def,
  value,
  onChange,
  onClear,
  variant = 'panel',
  label,
  listOp = 'in',
  onListOpChange,
  getOptionLabel,
}: FilterFieldProps) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const labelId = useId();
  const isColumn = variant === 'column';
  const listDef = def as ListFilterDef;
  const selected = (value as ListFilterValue | undefined)?.values ?? [];

  const getCode = (opt: string | LabeledCode): string =>
    typeof opt === 'string' ? opt : opt.code;

  const resolveLabel = useMemo(
    () => createLabelResolver(listDef.options, t, listDef.optionLabelPrefix, getOptionLabel),
    [listDef.options, listDef.optionLabelPrefix, getOptionLabel, t],
  );

  const renderValue = (vals: unknown) => {
    const arr = vals as string[];

    if (isColumn) {
      if (arr.length === 0)
        return <em style={{ fontSize: 'var(--filter-font-size)' }}>{t('table.filter.all')}</em>;
      if (arr.length === 1)
        return <span style={{ fontSize: 'var(--filter-font-size)' }}>{resolveLabel(arr[0] as string)}</span>;
      return (
        <span style={{ fontSize: 'var(--filter-font-size)' }}>
          {t('table.filter.selected', { count: arr.length })}
        </span>
      );
    }

    if (arr.length === 0)
      return (
        <Typography variant="body2" className="filter-placeholder">
          {t('table.filter.selectPlaceholder')}
        </Typography>
      );
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {arr.map(v => (
          <Chip
            key={v}
            label={resolveLabel(v as string)}
            size="small"
            color={listOp === 'in' ? 'primary' : 'error'}
            variant="outlined"
            sx={{ borderRadius: '8px', height: 20, fontSize: '0.7rem' }}
          />
        ))}
      </Box>
    );
  };

  const handleChange = (next: string[]) => {
    // '__all__' sentinel or empty array → clear the filter
    if (next.includes('__all__') || next.length === 0) {
      onClear();
    } else {
      onChange({ kind: 'list', values: next, op: listOp });
    }
  };

  const allItemSx = isColumn
    ? {
        ...menuItemSx(theme),
        fontStyle: 'italic',
      }
    : undefined;

  const selectEl = (
    <FormControl size="small" fullWidth>
      <Select
        multiple
        displayEmpty
        value={selected}
        onChange={e => handleChange(e.target.value as string[])}
        renderValue={renderValue}
        {...(isColumn
          ? {
              sx: selectSx(theme),
              MenuProps: { PaperProps: { sx: { maxHeight: 220, ...menuPaperSx(theme) } } },
            }
          : {
              className: 'filter-select',
              inputProps: { 'aria-labelledby': labelId },
              sx:
                listOp === 'not_in' && selected.length > 0
                  ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: `${theme.palette.error.main} !important` } }
                  : undefined,
              MenuProps: { PaperProps: { sx: { maxHeight: 220 } } },
            })}
      >
        {/* "All" option — selecting it clears the filter */}
        <MenuItem
          value="__all__"
          selected={selected.length === 0}
          {...(isColumn ? { sx: allItemSx } : { className: 'filter-menu-item' })}
        >
          <em>{t('table.filter.all')}</em>
        </MenuItem>
        {listDef.options.map(opt => (
          <MenuItem
            key={getCode(opt)}
            value={getCode(opt)}
            {...(isColumn ? { sx: menuItemSx(theme) } : { className: 'filter-menu-item' })}
          >
            {resolveLabel(opt)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (isColumn) return selectEl;

  return (
    <Box className="filter-field">
      <Box className="filter-list-label-row">
        <Typography variant="caption" className="filter-list-label" id={labelId}>
          {label}
        </Typography>
        <ToggleButtonGroup
          value={listOp}
          exclusive
          onChange={(_, v: ListOp | null) => {
            if (v !== null) onListOpChange?.(v);
          }}
          size="small"
        >
          <ToggleButton
            value="in"
            className="filter-toggle-btn"
            sx={{
              borderRadius: '6px 0 0 6px',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: theme.palette.common.white,
                borderColor: 'primary.main',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            }}
          >
            {t('table.filter.in')}
          </ToggleButton>
          <ToggleButton
            value="not_in"
            className="filter-toggle-btn"
            sx={{
              borderRadius: '0 6px 6px 0',
              '&.Mui-selected': {
                bgcolor: theme.palette.error.main,
                color: theme.palette.common.white,
                borderColor: theme.palette.error.main,
                '&:hover': { bgcolor: theme.palette.error.dark },
              },
            }}
          >
            {t('table.filter.notIn')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {selectEl}
      <FieldHint>{t('table.filter.availableCount', { count: listDef.options.length })}</FieldHint>
    </Box>
  );
}
